import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TextInput, ScrollView, TouchableOpacity, Modal, Platform, ActivityIndicator } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores, fuentes } from '../../styles/variablesCss';
import { localidades, obtenerCodigoPostal, obtenerBarriosPorCodigoPostal, obtenerLocalidadesPorCodigoPostal } from './LocationsData';
import { BACKEND_URL } from '../../Config/Const';
import FeedbackModal from '../Common/FeedbackModal';

const AddDireccion = ({ visible, onClose, onSaveDireccion, direccionExistente = null, modoEdicion = false }) => {
  const [nuevaDireccion, setNuevaDireccion] = useState({
    referencia: '',
    calle: '',
    numero: '',
    piso: '',
    localidad: '',
    barrio: '',
    celular: '',
    telefono: '',
    codPostal: '',
    codBarrio: ''
  });
  
  // Estados para controlar modales de selección en dispositivos móviles
  const [modalLocalidadVisible, setModalLocalidadVisible] = useState(false);
  const [modalBarrioVisible, setModalBarrioVisible] = useState(false);
  
  // Estado para los barrios y localidades disponibles
  const [barrios, setBarrios] = useState([]);
  const [localidadesDisponibles, setLocalidadesDisponibles] = useState([]);
  const [cargandoBarrios, setCargandoBarrios] = useState(false);
  // Estado para feedback (éxito / error)
  const [feedback, setFeedback] = useState({
    visible: false,
    type: 'success',
    title: '',
    message: '',
    onClose: null,
  });

  const openFeedback = ({ type = 'success', title = '', message = '', onClose = null }) => {
    setFeedback({ visible: true, type, title, message, onClose });
  };
  const closeFeedback = () => {
    const cb = feedback.onClose;
    setFeedback(prev => ({ ...prev, visible: false, onClose: null }));
    if (typeof cb === 'function') cb();
  };

  
  // Listener para detectar cambios en ciudadNom y actualizar la localidad
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleCiudadChange = async (e) => {
        if (e.detail?.key === 'ciudadNom' && e.detail?.newValue) {
          const nuevaCiudad = e.detail.newValue;
          const codigoPostal = await obtenerCodigoPostal(nuevaCiudad);
          
          if (codigoPostal) {
            // Actualizar localidades disponibles
            const localidadesFiltradas = obtenerLocalidadesPorCodigoPostal(codigoPostal);
            setLocalidadesDisponibles(localidadesFiltradas.length > 0 ? localidadesFiltradas : localidades);
            
            // Actualizar la dirección con la nueva ciudad
            setNuevaDireccion(prev => ({
              ...prev,
              localidad: nuevaCiudad,
              codPostal: codigoPostal,
              barrio: '',
              codBarrio: ''
            }));
            
            // Cargar barrios de la nueva ciudad
            cargarBarrios(codigoPostal);
          }
        }
      };

      window.addEventListener('localStorageChange', handleCiudadChange);

      return () => {
        window.removeEventListener('localStorageChange', handleCiudadChange);
      };
    }
  }, []);
  
  // Inicializar valores del formulario al montar el componente, cuando cambia direccionExistente o cuando se abre el modal
  useEffect(() => {
    const inicializarFormulario = async () => {
      try {
        // Si estamos en modo edición y tenemos una dirección existente
        if (modoEdicion && direccionExistente) {
          // Llenar el formulario con los datos de la dirección existente
          setNuevaDireccion({
            referencia: direccionExistente.descripcion || '',
            calle: direccionExistente.calle || '',
            numero: direccionExistente.nro || '', // nro en el JSON
            piso: direccionExistente.puerta || '', // puerta en el JSON
            localidad: direccionExistente.localidad || '',
            barrio: direccionExistente.barrio || '',
            celular: direccionExistente.cel || '', // cel en el JSON
            telefono: direccionExistente.tel || '', // tel en el JSON
            codPostal: direccionExistente.codPostal || '',
            codBarrio: direccionExistente.codBarrio || '',
            codDir: direccionExistente.codDir || direccionExistente.id || null // ID para edición
          });
          
          // Obtener localidades disponibles según el código postal
          const codPostalEdicion = direccionExistente.codPostal;
          if (codPostalEdicion) {
            const localidadesFiltradas = obtenerLocalidadesPorCodigoPostal(codPostalEdicion);
            setLocalidadesDisponibles(localidadesFiltradas.length > 0 ? localidadesFiltradas : localidades);
            
            // Cargar barrios para este código postal
            //cargarBarrios(codPostalEdicion);

          } else {
            // Si no hay código postal en la dirección existente, mostrar todas las localidades
            setLocalidadesDisponibles(localidades);
          }
          return; // Salir temprano en modo edición
        }
        
        // Comportamiento normal para nueva dirección
        let codigoPostal;
        let ciudadNom;
        
        // Obtener valores guardados (web o nativo)
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          codigoPostal = localStorage.getItem('codigoPostal');
          ciudadNom = localStorage.getItem('ciudadNom');
        } else {
          codigoPostal = await Storage.getItem('codigoPostal');
          ciudadNom = await Storage.getItem('ciudadNom');
        }
        
        // PRIORIDAD: Si hay ciudadNom, usarla para obtener el código postal
        if (ciudadNom) {
          const codPostalDeCiudad = obtenerCodigoPostal(ciudadNom);
          if (codPostalDeCiudad) {
            codigoPostal = codPostalDeCiudad;
          }
        }
        
        if (codigoPostal && ciudadNom) {
          // Filtrar localidades por código postal
          const localidadesFiltradas = obtenerLocalidadesPorCodigoPostal(codigoPostal);
          
          if (localidadesFiltradas.length > 0) {
            // Buscar la ciudad actual en las localidades filtradas
            const localidadEncontrada = localidadesFiltradas.find(
              loc => loc.nombre === ciudadNom
            );
            
            // Si la ciudad actual está en las localidades filtradas, usarla
            const localidadSeleccionada = localidadEncontrada ? ciudadNom : localidadesFiltradas[0].nombre;
            
            setLocalidadesDisponibles(localidadesFiltradas);
            setNuevaDireccion(prev => ({
              ...prev,
              codPostal: codigoPostal,
              localidad: localidadSeleccionada
            }));
          } else {
            setLocalidadesDisponibles(localidades);
          }
        } else {
          // Sin código postal o ciudad, mostrar todas
          setLocalidadesDisponibles(localidades);
        }
        
        // Cargar barrios iniciales

      } catch (error) {
        // Si falla, usar valores por defecto
        setLocalidadesDisponibles(localidades);
      }
    };
    
    // Ejecutar inicialización cuando se abre el modal (visible cambia a true)
    if (visible) {
      inicializarFormulario();
    }
  }, [modoEdicion, direccionExistente, visible]);
  
  // Actualizar el código postal cuando cambia la localidad y cargar barrios
  useEffect(() => {
    const actualizarCodigoPostal = async () => {
      if (!nuevaDireccion.localidad) return;
      
      const codPostal = obtenerCodigoPostal(nuevaDireccion.localidad);
      if (!codPostal) return;
      
      // Actualizar el código postal
      setNuevaDireccion(prev => ({
        ...prev,
        codPostal,
        barrio: '', // Resetear el barrio cuando cambia la localidad
        codBarrio: ''
      }));
      
      // Guardar para futuras consultas (compatible con web y nativo)
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        localStorage.setItem('codigoPostal', codPostal);
        localStorage.setItem('ciudadNom', nuevaDireccion.localidad);
      } else {
        await Storage.setItem('codigoPostal', codPostal);
        await Storage.setItem('ciudadNom', nuevaDireccion.localidad);
      }
      
      // Actualizar las localidades disponibles
      const localidadesFiltradas = obtenerLocalidadesPorCodigoPostal(codPostal);
      setLocalidadesDisponibles(localidadesFiltradas.length > 0 ? localidadesFiltradas : localidades);
      
      // Cargar los barrios para esta localidad
      cargarBarrios(codPostal);
    };
    
    actualizarCodigoPostal();
  }, [nuevaDireccion.localidad]);
  
  // Función para cargar barrios desde la API
  const cargarBarrios = async (codPostal = null) => {
    setCargandoBarrios(true);
    try {
      // Si no se proporciona código postal, la función lo obtendrá del localStorage
      const barriosData = await obtenerBarriosPorCodigoPostal(codPostal);
      setBarrios(barriosData);
      
      // Si hay barrios y existe un codBarrio en localStorage, seleccionar ese barrio
      if (Platform.OS === 'web' && typeof window !== 'undefined' && barriosData.length > 0) {
        const codBarrioGuardado = localStorage.getItem('codBarrio');
        if (codBarrioGuardado) {
          const barrioSeleccionado = barriosData.find(b => b.codigo.toString() === codBarrioGuardado.toString());
          if (barrioSeleccionado) {
            setNuevaDireccion(prev => ({
              ...prev,
              barrio: barrioSeleccionado.nombre,
              codBarrio: barrioSeleccionado.codigo
            }));
          }
        }
      }
    } catch (error) {
      setBarrios([]);
    } finally {
      setCargandoBarrios(false);
    }
  };
  
  // Función para seleccionar una localidad (para dispositivos móviles)
  const seleccionarLocalidad = (nombre) => {
    setNuevaDireccion({...nuevaDireccion, localidad: nombre});
    setModalLocalidadVisible(false);
  };
  
  // Función para seleccionar un barrio (para dispositivos móviles)
  const seleccionarBarrio = (barrio) => {
    setNuevaDireccion({...nuevaDireccion, barrio: barrio.nombre, codBarrio: barrio.codigo});
    
    // Guardar el código de barrio en localStorage
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      localStorage.setItem('codBarrio', barrio.codigo);
    }
    
    setModalBarrioVisible(false);
  };

  const resetForm = () => {
    setNuevaDireccion({
      referencia: '',
      calle: '',
      numero: '',
      piso: '',
      localidad: '',
      barrio: '',
      celular: '',
      telefono: '',
      codPostal: '', // Valor predeterminado para RAFAELA
      codBarrio: ''
    });
    
    // Cargar los barrios para RAFAELA (valor inicial)
    //cargarBarrios();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    // Validar que los campos requeridos estén completos
   /* if (!nuevaDireccion.calle || !nuevaDireccion.numero || !nuevaDireccion.localidad) {
      alert('Por favor completa los campos obligatorios: Calle, Número y Localidad');
      return;
    }
*/    
    // Determinar si estamos creando o editando
    const esEdicion = modoEdicion && nuevaDireccion.codDir;
    
    try {
      // Obtener datos de autenticación desde localStorage
      let mail = '';
      let codUsu = '';
      
      // Obtener datos de autenticación de forma consistente
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        mail = localStorage.getItem('mail') || '';
        codUsu = localStorage.getItem('codUsu') || '';
      } else {
        mail = await Storage.getItem('mail') || '';
        codUsu = await Storage.getItem('codUsu') || '';
      }
      
      if (!mail || !codUsu) {
        openFeedback({
          type: 'error',
          title: 'Sesión requerida',
          message: 'Debe iniciar sesión para guardar direcciones.',
        });
        return;
      }
      
      // Crear query params para la URL con todos los datos
      const queryParams = new URLSearchParams();
      // Datos de autenticación
      if (mail) queryParams.append('mail', mail);
      if (codUsu) queryParams.append('codUsu', codUsu);
      
      // Datos de la dirección
      queryParams.append('calle', nuevaDireccion.calle);
      queryParams.append('nro', nuevaDireccion.numero);
      if (nuevaDireccion.codBarrio) queryParams.append('codBarrio', nuevaDireccion.codBarrio);
      if (nuevaDireccion.piso) queryParams.append('piso', nuevaDireccion.piso);
      if (nuevaDireccion.codPostal) queryParams.append('codPostal', nuevaDireccion.codPostal);
      if (nuevaDireccion.referencia) queryParams.append('desDir', nuevaDireccion.referencia);
      if (nuevaDireccion.telefono) queryParams.append('tel', nuevaDireccion.telefono);
      if (nuevaDireccion.celular) queryParams.append('cel', nuevaDireccion.celular);
      
      // Si estamos editando, añadir codDir
      let metodo = 'POST';
      if (esEdicion && nuevaDireccion.codDir) {
        queryParams.append('codDir', nuevaDireccion.codDir);
        metodo = 'PUT';
      } else {
      }
      
      // URL completa con todos los parámetros
      const url = `${BACKEND_URL}/rest/usuarios/direcciones?${queryParams.toString()}`;
      
      const response = await fetch(url, {
        method: metodo,
        headers: {
          'Accept': 'application/json'
        }
        // No enviamos body ya que todos los datos van en la URL como QueryParams
      });
      
      if (!response.ok) {
        throw new Error(`Error al guardar dirección: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Si la respuesta es exitosa, notificar al componente padre
      if (data && data.dsResultado && Array.isArray(data.dsResultado.resultado) && 
          data.dsResultado.resultado[0]?.resultado === true) {
        
        // Crear objeto dirección para el componente padre con el codDir devuelto por la API
        const direccionActualizada = {
          ...nuevaDireccion,
          // Si estamos editando, mantener el mismo ID, si no, usar el devuelto por la API o generar uno
          id: esEdicion ? nuevaDireccion.codDir : (data.dsResultado.codDir || Date.now().toString())
        };
        
        // Enviar la dirección al componente padre
        onSaveDireccion(direccionActualizada, esEdicion);
        
        // Mostrar mensaje de éxito con cierre del modal al confirmar
        openFeedback({
          type: 'success',
          title: esEdicion ? 'Dirección actualizada' : 'Dirección guardada',
          message: esEdicion ? 'La dirección se actualizó correctamente.' : 'La dirección se guardó correctamente.',
          onClose: () => {
            // Reset del formulario y cerrar modal
            resetForm();
            onClose();
          },
        });
      } else {
        // Si hay un mensaje de error en la respuesta, mostrarlo
        const mensajeError = data?.dsResultado?.resultado[0]?.mensaje || 'Error al guardar la dirección';
        openFeedback({ type: 'error', title: 'No se pudo guardar', message: mensajeError });
      }
    } catch (error) {
      openFeedback({ type: 'error', title: 'Error', message: `Error: ${error.message}` });
    }
  };

  return (
    <>
      <Modal
        visible={visible}
        transparent={true}
        animationType="slide"
        onRequestClose={handleClose}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitulo}>{modoEdicion ? 'Editar dirección' : 'Nueva dirección'}</Text>
              <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
                <MaterialCommunityIcons name="close" size={22} color={colores.textoSecundario} />
              </TouchableOpacity>
            </View>
            
            <ScrollView style={styles.formScroll}>
              <Text style={styles.label}>Referencia: (Ej: Casa/Trabajo/Papá)</Text>
              <TextInput
                style={styles.input}
                placeholder="Ej: Casa, Trabajo, Casa de mamá..."
                value={nuevaDireccion.referencia}
                onChangeText={(text) => setNuevaDireccion({...nuevaDireccion, referencia: text})}
                placeholderTextColor={colores.grisClaro}
              />
              
              <View style={styles.rowContainer}>
                <View style={[styles.halfInputContainer, {width: '65%'}]}>
                  <Text style={styles.label}>Calle:</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="Nombre de la calle"
                    value={nuevaDireccion.calle}
                    onChangeText={(text) => setNuevaDireccion({...nuevaDireccion, calle: text})}
                    placeholderTextColor={colores.grisClaro}
                  />
                </View>
                
                <View style={[styles.halfInputContainer, {width: '30%'}]}>
                  <Text style={styles.label}>Nro.:</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="123"
                    value={nuevaDireccion.numero}
                    onChangeText={(text) => setNuevaDireccion({...nuevaDireccion, numero: text})}
                    keyboardType="number-pad"
                    placeholderTextColor={colores.grisClaro}
                  />
                </View>
              </View>

              <Text style={styles.label}>Piso y/o Dpto.:</Text>
              <TextInput
                style={styles.input}
                placeholder="Ej: 3B, Planta Baja, etc."
                value={nuevaDireccion.piso}
                onChangeText={(text) => setNuevaDireccion({...nuevaDireccion, piso: text})}
                placeholderTextColor={colores.grisClaro}
              />
              
              <View style={styles.rowContainer}>
                <View style={styles.halfInputContainer}>
                  <Text style={styles.label}>Localidad:</Text>
                  {Platform.OS === 'web' ? (
                    <select
                      value={nuevaDireccion.localidad}
                      onChange={(e) => setNuevaDireccion({...nuevaDireccion, localidad: e.target.value})}
                      style={styles.selectWeb}
                      disabled={true}
                    >
                      {localidadesDisponibles.map(loc => (
                        <option key={loc.nombre} value={loc.nombre}>{loc.nombre}</option>
                      ))}
                    </select>
                  ) : (
                    <TouchableOpacity 
                      style={[styles.selectMobile, styles.selectDisabled]} 
                      onPress={() => {}}
                      disabled={true}
                    >
                      <Text style={styles.selectText}>{nuevaDireccion.localidad}</Text>
                      <MaterialCommunityIcons name="lock" size={20} color={colores.textoSecundario} />
                    </TouchableOpacity>
                  )}
                </View>
                
                <View style={styles.halfInputContainer}>
                  <Text style={styles.label}>Barrio:</Text>
                  {cargandoBarrios ? (
                    <View style={styles.loadingContainer}>
                      <ActivityIndicator size="small" color={colores.rojo} />
                      <Text style={styles.loadingText}>Cargando barrios...</Text>
                    </View>
                  ) : Platform.OS === 'web' ? (
                    <select
                      value={nuevaDireccion.barrio}
                      onChange={(e) => {
                        const barrioSeleccionado = barrios.find(b => b.nombre === e.target.value);
                        const codBarrio = barrioSeleccionado ? barrioSeleccionado.codigo : '';
                        
                        setNuevaDireccion({
                          ...nuevaDireccion, 
                          barrio: e.target.value,
                          codBarrio: codBarrio
                        });
                        
                        // Guardar el código de barrio en localStorage
                        if (codBarrio && typeof window !== 'undefined') {
                          localStorage.setItem('codBarrio', codBarrio);
                        }
                      }}
                      style={styles.selectWeb}
                      disabled={barrios.length === 0}
                    >
                      <option value="">Seleccione Barrio</option>
                      {barrios.map(barrio => (
                        <option key={barrio.codigo} value={barrio.nombre}>
                          {barrio.nombre}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <TouchableOpacity 
                      style={styles.selectMobile}
                      onPress={() => barrios.length > 0 && setModalBarrioVisible(true)}
                      disabled={barrios.length === 0}
                    >
                      <Text style={[styles.selectText, barrios.length === 0 && styles.selectTextDisabled]}>
                        {nuevaDireccion.barrio || (barrios.length === 0 ? 'No hay barrios disponibles' : 'Seleccione Barrio')}
                      </Text>
                      {barrios.length > 0 && (
                        <MaterialCommunityIcons name="chevron-down" size={20} color={colores.textoSecundario} />
                      )}
                    </TouchableOpacity>
                  )}
                </View>
              </View>

              <View style={styles.rowContainer}>
                <View style={styles.halfInputContainer}>
                  <Text style={styles.label}>Celular:</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="(Ej: 3492666666)(Sin 0,sin 15)"
                    value={nuevaDireccion.celular}
                    onChangeText={(text) => setNuevaDireccion({...nuevaDireccion, celular: text})}
                    keyboardType="phone-pad"
                    placeholderTextColor={colores.grisClaro}
                  />
                </View>
                
                <View style={styles.halfInputContainer}>
                  <Text style={styles.label}>Teléfono fijo:</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="(Ej: 03492506104)"
                    value={nuevaDireccion.telefono}
                    onChangeText={(text) => setNuevaDireccion({...nuevaDireccion, telefono: text})}
                    keyboardType="phone-pad"
                    placeholderTextColor={colores.grisClaro}
                  />
                </View>
              </View>
            </ScrollView>
            
            <TouchableOpacity style={styles.guardarDireccionBoton} onPress={handleSave}>
              <Text style={styles.guardarDireccionTexto}>{modoEdicion ? 'Actualizar dirección' : 'Guardar dirección'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
      
      {/* Modal para selección de localidad en dispositivos móviles */}
      {Platform.OS !== 'web' && (
        <Modal
          visible={modalLocalidadVisible}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setModalLocalidadVisible(false)}
        >
          <View style={styles.modalContainer}>
            <View style={[styles.modalContent, styles.selectorModalContent]}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitulo}>Seleccionar localidad</Text>
                <TouchableOpacity onPress={() => setModalLocalidadVisible(false)} style={styles.closeButton}>
                  <MaterialCommunityIcons name="close" size={22} color={colores.textoSecundario} />
                </TouchableOpacity>
              </View>
              
              <ScrollView style={styles.opcionesList}>
                {localidadesDisponibles.map(loc => (
                  <TouchableOpacity
                    key={loc.nombre}
                    style={styles.opcionItem}
                    onPress={() => seleccionarLocalidad(loc.nombre)}
                  >
                    <Text style={[styles.opcionTexto, nuevaDireccion.localidad === loc.nombre && styles.opcionSeleccionada]}>
                      {loc.nombre}
                    </Text>
                    {nuevaDireccion.localidad === loc.nombre && (
                      <MaterialCommunityIcons name="check" size={20} color={colores.rojo} />
                    )}
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}
      
      {/* Modal para selección de barrio en dispositivos móviles */}
      {Platform.OS !== 'web' && (
        <Modal
          visible={modalBarrioVisible}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setModalBarrioVisible(false)}
        >
          <View style={styles.modalContainer}>
            <View style={[styles.modalContent, styles.selectorModalContent]}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitulo}>Seleccionar barrio</Text>
                <TouchableOpacity onPress={() => setModalBarrioVisible(false)} style={styles.closeButton}>
                  <MaterialCommunityIcons name="close" size={22} color={colores.textoSecundario} />
                </TouchableOpacity>
              </View>
              
              <ScrollView style={styles.opcionesList}>
                {barrios.map(barrio => (
                  <TouchableOpacity
                    key={barrio.codigo}
                    style={styles.opcionItem}
                    onPress={() => seleccionarBarrio(barrio)}
                  >
                    <Text style={[styles.opcionTexto, nuevaDireccion.barrio === barrio.nombre && styles.opcionSeleccionada]}>
                      {barrio.nombre}
                    </Text>
                    {nuevaDireccion.barrio === barrio.nombre && (
                      <MaterialCommunityIcons name="check" size={20} color={colores.rojo} />
                    )}
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}
      {/* Feedback (éxito / error) */}
      <FeedbackModal
        visible={feedback.visible}
        type={feedback.type}
        title={feedback.title}
        message={feedback.message}
        onClose={closeFeedback}
        confirmText={feedback.type === 'success' ? 'Continuar' : 'Entendido'}
      />
    </>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalContent: {
    width: '90%',
    maxWidth: 600,
    backgroundColor: 'white',
    padding: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  modalTitulo: {
    fontSize: 18,
    fontFamily: fuentes.bold,
    color: colores.textoPrincipal,
  },
  closeButton: {
    padding: 5,
  },
  formScroll: {
    maxHeight: 450,
    marginBottom: 15,
  },
  label: {
    fontFamily: fuentes.semiBold,
    marginBottom: 5,
    color: colores.textoPrincipal,
    fontSize: 14,
  },
  input: {
    borderWidth: 1,
    borderColor: colores.borde,
    borderRadius: 5,
    padding: 10,
    marginBottom: 15,
    fontFamily: fuentes.regular,
    color: colores.textoPrincipal,
  },
  rowContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  halfInputContainer: {
    width: '48%',
  },
  selectWeb: {
    borderWidth: 1,
    borderColor: colores.borde,
    borderRadius: 5,
    padding: 10,
    marginBottom: 15,
    width: '100%',
    height: 42,
    fontFamily: fuentes.regular,
    backgroundColor: 'white',
    color: colores.textoPrincipal,
  },
  selectMobile: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colores.borde,
    borderRadius: 5,
    padding: 10,
    marginBottom: 15,
    backgroundColor: 'white',
  },
  selectText: {
    fontFamily: fuentes.regular,
    color: colores.textoPrincipal,
  },
  guardarDireccionBoton: {
    backgroundColor: colores.rojo,
    borderRadius: 5,
    padding: 12,
    alignItems: 'center',
    marginTop: 5,
  },
  guardarDireccionTexto: {
    color: 'white',
    fontFamily: fuentes.bold,
    fontSize: 16,
  },
  selectorModalContent: {
    maxHeight: '60%',
  },
  opcionesList: {
    maxHeight: 300,
  },
  opcionItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: colores.borde,
  },
  opcionTexto: {
    fontSize: 15,
    fontFamily: fuentes.regular,
    color: colores.textoPrincipal,
  },
  opcionSeleccionada: {
    color: colores.rojo,
    fontFamily: fuentes.semiBold,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: colores.borde,
    borderRadius: 5,
    marginBottom: 15,
    backgroundColor: colores.fondoClaro,
  },
  loadingText: {
    marginLeft: 10,
    fontFamily: fuentes.regular,
    color: colores.textoSecundario,
    fontSize: 14,
  },
  selectTextDisabled: {
    color: colores.textoSecundario,
    opacity: 0.6,
  },
  selectDisabled: {
    backgroundColor: colores.fondoClaro,
    opacity: 0.7,
  },
});

export default AddDireccion;
